import { isLocalDevHost } from '@core/utils/isLocalDevHost';

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const cookies = document.cookie.split(';');
  const found = cookies.find(c => c.trim().startsWith(`${name}=`));
  if (!found) return null;
  return decodeURIComponent(found.split('=')[1] ?? '') || null;
}

function getCookieWriteOpts(): {
  domain?: string;
  path: string;
  secure: boolean;
  sameSite: 'Lax' | 'Strict' | 'None';
} {
  if (isLocalDevHost()) {
    return { path: '/', secure: false, sameSite: 'Lax' };
  }
  return {
    domain: '.play-studios.ai',
    path: '/',
    secure: true,
    sameSite: 'Lax',
  };
}

function getCookieDeleteDomains(): Array<string | undefined> {
  if (isLocalDevHost()) {
    return [undefined, '.play-studios.ai'];
  }
  return ['.play-studios.ai'];
}

function writeCookie(
  name: string,
  value: string,
  opts?: {
    domain?: string;
    path?: string;
    expiresISO?: string;
    secure?: boolean;
    sameSite?: 'Lax' | 'Strict' | 'None';
  }
) {
  if (typeof document === 'undefined') return;
  const defaults = getCookieWriteOpts();
  const path = opts?.path ?? defaults.path;
  let cookie = `${name}=${encodeURIComponent(value)}; path=${path}`;
  const domain = opts?.domain ?? defaults.domain;
  if (domain) {
    cookie += `; domain=${domain}`;
  }
  if (opts?.expiresISO) {
    const expires = new Date(opts.expiresISO).toUTCString();
    cookie += `; expires=${expires}`;
  }
  const secure = opts?.secure ?? defaults.secure;
  if (secure) cookie += '; Secure';
  const sameSite = opts?.sameSite ?? defaults.sameSite;
  if (sameSite) cookie += `; SameSite=${sameSite}`;
  document.cookie = cookie;
}

function deleteCookie(name: string, opts?: { domain?: string; path?: string }) {
  if (typeof document === 'undefined') return;
  const path = opts?.path ?? '/';
  const domain = opts?.domain;
  let cookie = `${name}=; path=${path}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  if (domain) {
    cookie += `; domain=${domain}`;
  }
  document.cookie = cookie;
}

export type GameSessionCookies = {
  playerSessionId: string;
  gameSessionId: string;
  gameId: number;
  currency?: string;
  slug?: string;
};

/** In-memory session from the latest successful /game/launch (authoritative). */
let activeGameSession: GameSessionCookies | null = null;

function readLocalStorage(key: string): string | null {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      return window.localStorage.getItem(key);
    }
  } catch {
    // ignore
  }
  return null;
}

export function getPlayerSessionId(): string | null {
  return (
    activeGameSession?.playerSessionId ??
    readCookie('playerSessionId') ??
    readLocalStorage('playgrid-player-session-id')
  );
}

export function getGameSessionId(): string | null {
  return (
    activeGameSession?.gameSessionId ??
    readCookie('gameSessionId') ??
    readLocalStorage('playgrid-game-session-id')
  );
}

export function getLaunchedGameId(): number | null {
  if (activeGameSession?.gameId != null) {
    return activeGameSession.gameId;
  }
  const raw = readCookie('launchedGameId');
  if (!raw) {
    const stored = readLocalStorage('playgrid-launched-game-id');
    if (!stored) return null;
    const parsed = Number(stored);
    return Number.isFinite(parsed) ? parsed : null;
  }
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

export function getGameSessionSlug(): string | null {
  return (
    activeGameSession?.slug ?? readLocalStorage('playgrid-game-session-slug')
  );
}

export function setGameSession(session: GameSessionCookies): void {
  activeGameSession = session;
  const cookieOpts = getCookieWriteOpts();

  writeCookie('playerSessionId', session.playerSessionId, cookieOpts);
  writeCookie('gameSessionId', session.gameSessionId, cookieOpts);
  writeCookie('launchedGameId', String(session.gameId), cookieOpts);
  if (session.currency) {
    writeCookie('userCurrency', session.currency, cookieOpts);
  }

  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(
        'playgrid-player-session-id',
        session.playerSessionId
      );
      window.localStorage.setItem(
        'playgrid-game-session-id',
        session.gameSessionId
      );
      window.localStorage.setItem(
        'playgrid-launched-game-id',
        String(session.gameId)
      );
      if (session.slug) {
        window.localStorage.setItem('playgrid-game-session-slug', session.slug);
      }
    }
  } catch {
    // ignore
  }
}

export function clearGameSession(): void {
  activeGameSession = null;

  for (const domain of getCookieDeleteDomains()) {
    [
      'playerSessionId',
      'gameSessionId',
      'launchedGameId',
      'userCurrency',
    ].forEach(name => {
      deleteCookie(name, { domain, path: '/' });
    });
  }

  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.removeItem('playgrid-player-session-id');
      window.localStorage.removeItem('playgrid-game-session-id');
      window.localStorage.removeItem('playgrid-launched-game-id');
      window.localStorage.removeItem('playgrid-game-session-slug');
    }
  } catch {
    // ignore
  }
}
